import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Topbar from "../../../components/Topbar";
import api from "../../../../utils/axios";
import Swal from "sweetalert2";
import { getDataWithComp } from "../../../../utils/SamuiUtils";

// Helper: format comma (เหมือนใบเสนอราคา)
function formatWithCommas(numStr) {
  if (numStr === null || numStr === undefined || numStr === "") return "";
  const parts = numStr.toString().split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
}

// NumberInput component เหมือนใบเสนอราคา
const NumberInput = ({ value, onValueChange, className = "", style = {} }) => {
  const [localValue, setLocalValue] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      if (value === null || value === undefined || value === "") {
        setLocalValue("");
      } else {
        setLocalValue(formatWithCommas(parseFloat(value).toFixed(2)));
      }
    }
  }, [value]);

  const handleFocus = (e) => e.target.select();

  const handleChange = (e) => {
    let raw = e.target.value.replace(/,/g, "");
    if (!/^\d*\.?\d*$/.test(raw)) return;
    const parts = raw.split(".");
    if (parts[1]?.length > 2) return;
    let displayVal = raw;
    if (parts[0].length > 3) displayVal = formatWithCommas(raw);
    setLocalValue(displayVal);
    const numericValue = parseFloat(raw) || 0;
    onValueChange(numericValue);
  };

  const handleBlur = () => {
    const numericValue = parseFloat(localValue.replace(/,/g, "")) || 0;
    const formatted = formatWithCommas(numericValue.toFixed(2));
    setLocalValue(formatted);
    onValueChange(numericValue);
  };

  return (
    <input
      ref={inputRef}
      type="text"
      value={localValue}
      onFocus={handleFocus}
      onChange={handleChange}
      onBlur={handleBlur}
      className={className}
      style={style}
      inputMode="decimal"
      placeholder="0.00"
    />
  );
};

const SearchableSelect = ({ value, options, onChange, disabled = false }) => {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const selected = options.find((o) => o.Item_Id === value) || null;

  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const filtered = search
    ? options.filter(
        (o) =>
          (o.Item_Code || "").toLowerCase().includes(search.toLowerCase()) ||
          (o.Item_Name || "").toLowerCase().includes(search.toLowerCase()) ||
          (o.Item_Short_Name || "").toLowerCase().includes(search.toLowerCase())
      )
    : options;

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <div style={{ display: "flex", border: "1px solid #dee2e6", borderRadius: "8px", overflow: "hidden", height: "38px", backgroundColor: disabled ? "#f9fafb" : "#fff" }}>
        <input
          type="text"
          value={isOpen ? search : (selected ? `${selected.Item_Code} - ${selected.Item_Name}` : "")}
          onChange={(e) => { setSearch(e.target.value); setIsOpen(true); }}
          onFocus={() => { setIsOpen(true); setSearch(""); }}
          placeholder={isOpen ? "พิมพ์รหัส/ชื่อสินค้า..." : "-- เลือกสินค้า --"}
          disabled={disabled}
          style={{ flex: 1, border: "none", padding: "0 10px", outline: "none", fontSize: "14px", backgroundColor: "transparent", height: "100%", lineHeight: "38px" }}
        />
        {value && !disabled && (
          <button type="button" onClick={() => { onChange(null); setSearch(""); setIsOpen(false); }}
            style={{ border: "none", background: "none", padding: "0 6px", color: "#9ca3af", cursor: "pointer", fontSize: "16px" }}>×</button>
        )}
        <button type="button" onClick={() => { if (!disabled) setIsOpen((v) => !v); }}
          style={{ border: "none", borderLeft: "1px solid #dee2e6", background: "#f9fafb", padding: "0 10px", cursor: disabled ? "default" : "pointer", color: "#6b7280" }}
          disabled={disabled}>▾</button>
      </div>
      {isOpen && !disabled && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1050, backgroundColor: "#fff", border: "1px solid #dee2e6", borderRadius: "8px", maxHeight: "220px", overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.12)", marginTop: 2 }}>
          <div onClick={() => { onChange(null); setSearch(""); setIsOpen(false); }}
            style={{ padding: "8px 12px", cursor: "pointer", color: "#9ca3af", fontSize: "14px" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
            -- เลือกสินค้า --
          </div>
          {filtered.map((item) => (
            <div key={item.Item_Id} onClick={() => { onChange(item); setSearch(""); setIsOpen(false); }}
              style={{ padding: "8px 12px", cursor: "pointer", fontSize: "14px", backgroundColor: value === item.Item_Id ? "#fef3c7" : "transparent" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
              onMouseLeave={(e) => (e.currentTarget.style.background = value === item.Item_Id ? "#fef3c7" : "transparent")}>
              <span style={{ fontWeight: 600 }}>{item.Item_Code}</span> - {item.Item_Name}
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{ padding: "8px 12px", color: "#9ca3af", fontSize: "14px" }}>ไม่พบสินค้า</div>
          )}
        </div>
      )}
    </div>
  );
};

const ServiceStepForm = ({
  callInitialize,
  goBackToMain,
  mode,
  name,
  formData,
  setFormData,
  selectedStep,
  selectedService,
  setMode,
  stepList = [],
  returnToFormData,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const isSavingRef = useRef(false);
  const [itemList, setItemList] = useState([]);
  const [groupList, setGroupList] = useState([]);
  const [editSelectedGroup, setEditSelectedGroup] = useState("");
  const [formDataList, setFormDataList] = useState([]);

  const goBackToList = async () => {
    // If goBackToMain is provided and we have a selectedService, use it to preserve selection
    if (typeof goBackToMain === "function" && selectedService) {
      try {
        await goBackToMain();
        return;
      } catch (err) {
        console.error("goBackToMain failed:", err);
      }
    }

    // Fallback to initialize if goBackToMain is not available
    if (typeof callInitialize === "function") {
      try {
        await callInitialize();
        return;
      } catch (err) {
        console.error("callInitialize failed:", err);
      }
    }

    if (typeof setMode === "function") {
      setMode("S");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [items, groups] = await Promise.all([
          getDataWithComp(
            "View_Set_Item",
            "ORDER BY Item_Short_Name, Item_Name",
            { fields: ["Item_Id", "Item_Code", "Item_Name", "Item_Short_Name", "Item_Unit_ST", "Item_Group"] }
          ),
          getDataWithComp("Tb_Set_ItmGroup", "AND Group_Status = 'Y' ORDER BY Group_Name ASC"),
        ]);
        setItemList(Array.isArray(items) ? items : []);
        setGroupList(Array.isArray(groups) ? groups : []);
      } catch (error) {
        console.error("Error fetching items:", error);
      }
    };

    fetchData();
  }, []);

  // Pre-select group for update mode based on current item
  useEffect(() => {
    if (mode === "U" && formData.Item_Id && itemList.length > 0) {
      const item = itemList.find((i) => i.Item_Id === formData.Item_Id);
      if (item?.Item_Group) {
        setEditSelectedGroup(String(item.Item_Group));
      }
    }
  }, [mode, formData.Item_Id, itemList]);

  // Initialize formDataList when mode changes to insert
  useEffect(() => {
    if (mode === "I") {
      // If returning from StepUnit with formDataList, restore it
      if (returnToFormData && Array.isArray(returnToFormData) && returnToFormData.length > 0) {
        setFormDataList(returnToFormData);
      } else if (formDataList.length === 0) {
        // Only initialize if formDataList is empty (to preserve data when returning from StepUnit)
        const baseOrder = stepList.length > 0 ? Math.max(...stepList.map(s => s.Step_Order || 0)) + 1 : 1;
        setFormDataList([{
          Service_Id: formData.Service_Id,
          Step_Title: "",
          Step_Description: "",
          Step_Order: baseOrder,
          Step_Qty: "",
          Step_Unit: "",
          IsDefault: 0,
          IsActive: 1,
          Comp_Id: formData.Comp_Id,
        }]);
      }
    } else {
      // For update mode, clear formDataList
      setFormDataList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, returnToFormData]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.target?.tagName === "INPUT" ||
        event.target?.tagName === "TEXTAREA" ||
        event.target?.tagName === "SELECT"
      ) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        goBackToList();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const addForm = () => {
    const baseOrder = stepList.length > 0 ? Math.max(...stepList.map(s => s.Step_Order || 0)) + 1 : 1;
    const maxOrder = formDataList.length > 0 
      ? Math.max(...formDataList.map(f => f.Step_Order || baseOrder))
      : baseOrder - 1;
    
    setFormDataList([...formDataList, {
      Service_Id: formData.Service_Id,
      Step_Title: "",
      Step_Description: "",
      Step_Order: maxOrder + 1,
      Step_Qty: "",
      Step_Unit: "",
      IsDefault: 0,
      IsActive: 1,
      Comp_Id: formData.Comp_Id,
    }]);
  };

  const removeForm = (index) => {
    if (formDataList.length > 1) {
      setFormDataList(formDataList.filter((_, i) => i !== index));
    } else {
      Swal.fire("แจ้งเตือน", "ต้องมีอย่างน้อย 1 ฟอร์ม", "warning");
    }
  };

  const updateFormData = (index, field, value) => {
    const updated = formDataList.map((form, i) => {
      if (i === index) {
        return { ...form, [field]: value };
      }
      return form;
    });
    setFormDataList(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    setLoading(true);

    try {
      const emp_id = localStorage.getItem("emp_id");
      const emp_name = localStorage.getItem("emp_name");

      if (mode === "I") {
        // Validate all forms
        const invalidForms = formDataList.filter(
          (f) => !f.Step_Title || !f.Step_Title.trim()
        );
        if (invalidForms.length > 0) {
          Swal.fire("แจ้งเตือน", "กรุณากรอกชื่อขั้นตอนให้ครบทุกฟอร์ม", "warning");
          return;
        }

        // Create all forms
        const promises = formDataList.map((form) =>
          api.post("/create-service-step", {
            Service_Id: form.Service_Id,
            Step_Title: form.Step_Title,
            Step_Description: form.Step_Description || null,
            Step_Order: form.Step_Order || 0,
            Step_Qty: form.Step_Qty || null,
            Step_Unit: form.Step_Unit || null,
            Item_Id: form.Item_Id || null,
            Item_Code: form.Item_Code || null,
            Item_Name: form.Item_Name || null,
            IsActive: form.IsActive !== undefined ? form.IsActive : 1,
            IsDefault: form.IsDefault !== undefined ? form.IsDefault : 0,
            Comp_Id: form.Comp_Id,
            Created_By_Id: emp_id,
            Created_By_Name: emp_name,
          })
        );

        const results = await Promise.all(promises);
        const failedResults = results.filter((r) => r.data.status !== "OK");

        if (failedResults.length > 0) {
          throw new Error(
            failedResults[0].data.message || "ไม่สามารถสร้างขั้นตอนการบริการได้"
          );
        }

        Swal.fire(
          "สำเร็จ!",
          `สร้างขั้นตอนการบริการสำเร็จ ${formDataList.length} รายการ`,
          "success"
        );
        await goBackToList();
      } else if (mode === "U") {
        // Update single step
        const res = await api.put(`/update-service-step/${formData.Step_Id}`, {
          Step_Title: formData.Step_Title,
          Step_Description: formData.Step_Description || null,
          Step_Order: formData.Step_Order || 0,
          Step_Qty: formData.Step_Qty || null,
          Step_Unit: formData.Step_Unit || null,
          Item_Id: formData.Item_Id || null,
          Item_Code: formData.Item_Code || null,
          Item_Name: formData.Item_Name || null,
          IsActive: formData.IsActive !== undefined ? formData.IsActive : 1,
          IsDefault: formData.IsDefault !== undefined ? formData.IsDefault : 0,
          Update_By_Id: emp_id,
          Update_By_Name: emp_name,
        });

        if (res.data.status === "OK") {
          Swal.fire("สำเร็จ!", "แก้ไขขั้นตอนการบริการสำเร็จ", "success");
          await goBackToList();
        } else {
          throw new Error(res.data.message || "ไม่สามารถแก้ไขขั้นตอนการบริการได้");
        }
      }
    } catch (error) {
      console.error("Error saving service step:", error);
      Swal.fire(
        "ผิดพลาด!",
        error.response?.data?.message || error.message || "ไม่สามารถบันทึกข้อมูลได้",
        "error"
      );
    } finally {
      isSavingRef.current = false;
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedStep?.Step_Id) return;

    const result = await Swal.fire({
      title: "ยืนยันการลบ",
      text: "คุณต้องการลบขั้นตอนการบริการนี้หรือไม่?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#E13F57",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "ลบ",
      cancelButtonText: "ยกเลิก",
    });

    if (!result.isConfirmed) return;

    setLoading(true);
    try {
      const res = await api.delete(`/delete-service-step/${selectedStep.Step_Id}`);
      if (res.data.status === "OK") {
        Swal.fire("สำเร็จ!", "ลบขั้นตอนการบริการสำเร็จ", "success");
        await goBackToList();
      } else {
        throw new Error(res.data.message || "ไม่สามารถลบขั้นตอนการบริการได้");
      }
    } catch (error) {
      console.error("Error deleting service step:", error);
      Swal.fire(
        "ผิดพลาด!",
        error.response?.data?.message || error.message || "ไม่สามารถลบข้อมูลได้",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Topbar breadcrumbs={[{ label: name }]} />
      <div className="page-header d-flex justify-content-between align-items-center mb-3">
        <Breadcrumbs
          page={mode === "I" ? `สร้าง${name}` : `แก้ไข${name}`}
          items={[
            { name: "ข้อมูลตั้งต้น", url: "/initialdata" },
            { name: "ข้อมูลบริการ", url: "/service" },
            { name: "ขั้นตอนการบริการ", url: "/servicestep" },
            { name: mode === "I" ? `สร้าง${name}` : `แก้ไข${name}` },
          ]}
        />
      </div>

      <form onSubmit={handleSubmit}>
        {/* Service Info (Read-only) */}
        <div className="card shadow-sm border-0 mb-3" style={{ borderRadius: "12px" }}>
          <div className="card-body p-4">
            <div className="mb-0">
              <label className="form-label fw-bold">บริการ</label>
              <input
                type="text"
                className="form-control"
                value={selectedService?.Service_Name || ""}
                disabled
                style={{ backgroundColor: "#f9fafb" }}
              />
            </div>
          </div>
        </div>

        {/* Multiple Forms for Insert Mode */}
        {mode === "I" ? (
          <>
            {formDataList.map((form, index) => (
              <div key={index} className="card shadow-sm border-0 mb-3" style={{ borderRadius: "12px" }}>
                <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: "#f8f9fa", borderRadius: "12px 12px 0 0" }}>
                  <h6 className="mb-0 fw-bold">ฟอร์มที่ {index + 1}</h6>
                  {formDataList.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeForm(index)}
                      className="btn btn-sm"
                      style={{
                        backgroundColor: "#dc2626",
                        color: "#fff",
                        borderRadius: "6px",
                        padding: "4px 12px",
                        border: "none",
                      }}
                    >
                      <i className="fa fa-trash"></i> ลบ
                    </button>
                  )}
                </div>
                <div className="card-body p-4">
                  {/* Row 1: ลำดับ + ชื่อขั้นตอน + IsDefault + สถานะ */}
                  <div className="row mb-4">
                    <div className="col-md-1">
                      <label className="form-label fw-bold">ลำดับ</label>
                      <input
                        type="number"
                        className="form-control"
                        value={form.Step_Order || 0}
                        onChange={(e) => updateFormData(index, "Step_Order", parseInt(e.target.value) || 0)}
                        min="0"
                        style={{ borderRadius: "8px" }}
                      />
                    </div>
                    <div className="col-md-7">
                      <label className="form-label fw-bold">
                        ชื่อขั้นตอน <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={form.Step_Title || ""}
                        onChange={(e) => updateFormData(index, "Step_Title", e.target.value)}
                        required
                        maxLength={500}
                        style={{ borderRadius: "8px" }}
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label fw-bold">สถานะ</label>
                      <select
                        className="form-select"
                        value={form.IsActive !== undefined ? form.IsActive : 1}
                        onChange={(e) => updateFormData(index, "IsActive", parseInt(e.target.value))}
                        style={{ borderRadius: "8px", height: "38px" }}
                      >
                        <option value={1}>ใช้งาน</option>
                        <option value={0}>ไม่ใช้งาน</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label fw-bold d-block mb-2" style={{ visibility: "hidden" }}>_</label>
                      <div className="form-check d-flex align-items-center" style={{ marginTop: "8px" }}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`isDefault-${index}`}
                          checked={form.IsDefault === 1}
                          onChange={(e) => updateFormData(index, "IsDefault", e.target.checked ? 1 : 0)}
                          style={{ cursor: "pointer", marginTop: "0" }}
                        />
                        <label className="form-check-label ms-2 mb-0" htmlFor={`isDefault-${index}`} style={{ cursor: "pointer" }}>
                          กำหนดเป็นค่าเริ่มต้น
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Row 2: หมวดหมู่ + สินค้า + ปริมาณ + หน่วย */}
                  <div className="row mb-4 align-items-end">
                    <div className="col-md-3">
                      <label className="form-label fw-bold">หมวดหมู่</label>
                      <select
                        className="form-select"
                        value={form._selectedGroup || ""}
                        onChange={(e) => {
                          // เมื่อเปลี่ยนหมวดหมู่ ล้างสินค้าที่เลือกไว้
                          setFormDataList(formDataList.map((f, i) =>
                            i === index ? { ...f, _selectedGroup: e.target.value, Item_Id: null, Item_Code: null, Item_Name: null, Step_Unit: "" } : f
                          ));
                        }}
                        style={{ borderRadius: "8px", height: "38px" }}
                      >
                        <option value="">-- ทุกหมวดหมู่ --</option>
                        {groupList.map((g) => (
                          <option key={g.Group_Id} value={g.Group_Id}>
                            {g.Group_Name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-bold">สินค้า</label>
                      <SearchableSelect
                        value={form.Item_Id || null}
                        options={itemList.filter((item) => !form._selectedGroup || String(item.Item_Group) === String(form._selectedGroup))}
                        onChange={(selected) => {
                          setFormDataList(formDataList.map((f, i) =>
                            i === index ? { ...f, Item_Id: selected?.Item_Id || null, Item_Code: selected?.Item_Code || null, Item_Name: selected?.Item_Name || null, Step_Unit: selected?.Item_Unit_ST || f.Step_Unit } : f
                          ));
                        }}
                      />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label fw-bold">ปริมาณ</label>
                      <NumberInput
                        value={form.Step_Qty || ""}
                        onValueChange={(value) => updateFormData(index, "Step_Qty", value)}
                        className="form-control text-end"
                        style={{ borderRadius: "8px", height: "38px" }}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fw-bold">หน่วย</label>
                      <input
                        type="text"
                        className="form-control"
                        value={form.Step_Unit || ""}
                        disabled
                        placeholder="หน่วยจากสินค้า"
                        style={{ borderRadius: "8px", height: "38px", backgroundColor: "#f9fafb" }}
                      />
                    </div>
                  </div>

                  {/* Step Description */}
                  <div className="mb-4">
                    <label className="form-label fw-bold text-nowrap">
                      รายละเอียดขั้นตอน
                    </label>
                    <textarea
                      className="form-control"
                      value={form.Step_Description || ""}
                      onChange={(e) => updateFormData(index, "Step_Description", e.target.value)}
                      rows={5}
                      style={{ borderRadius: "8px" }}
                      placeholder="ระบุรายละเอียดเพิ่มเติมของขั้นตอนการบริการ..."
                    />
                  </div>
                </div>
              </div>
            ))}

            {/* Add Form Button */}
            <div className="mb-3">
              <button
                type="button"
                onClick={addForm}
                className="btn w-100"
                style={{
                  backgroundColor: "#10b981",
                  color: "#fff",
                  borderRadius: "8px",
                  padding: "12px",
                  border: "none",
                  fontWeight: "500",
                }}
              >
                <i className="fa fa-plus me-2"></i>
                เพิ่มขั้นตอน
              </button>
            </div>
          </>
        ) : (
          /* Single Form for Update Mode */
          <div className="card shadow-sm border-0" style={{ borderRadius: "12px" }}>
            <div className="card-body p-4">
              {/* Row 1: ลำดับ + ชื่อขั้นตอน + สถานะ + IsDefault */}
              <div className="row mb-4">
                <div className="col-md-1">
                  <label className="form-label fw-bold">ลำดับ</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.Step_Order || 0}
                    onChange={(e) =>
                      setFormData({ ...formData, Step_Order: parseInt(e.target.value) || 0 })
                    }
                    min="0"
                    style={{ borderRadius: "8px" }}
                  />
                </div>
                <div className="col-md-7">
                  <label className="form-label fw-bold">
                    ชื่อขั้นตอน <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.Step_Title || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, Step_Title: e.target.value })
                    }
                    required
                    maxLength={500}
                    style={{ borderRadius: "8px" }}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-bold">สถานะ</label>
                  <select
                    className="form-select"
                    value={formData.IsActive !== undefined ? formData.IsActive : 1}
                    onChange={(e) =>
                      setFormData({ ...formData, IsActive: parseInt(e.target.value) })
                    }
                    style={{ borderRadius: "8px", height: "38px" }}
                  >
                    <option value={1}>ใช้งาน</option>
                    <option value={0}>ไม่ใช้งาน</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-bold d-block mb-2" style={{ visibility: "hidden" }}>_</label>
                  <div className="form-check d-flex align-items-center" style={{ marginTop: "8px" }}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="isDefault"
                      checked={formData.IsDefault === 1}
                      onChange={(e) =>
                        setFormData({ ...formData, IsDefault: e.target.checked ? 1 : 0 })
                      }
                      style={{ cursor: "pointer", marginTop: "0" }}
                    />
                    <label className="form-check-label ms-2 mb-0" htmlFor="isDefault" style={{ cursor: "pointer" }}>
                      กำหนดเป็นค่าเริ่มต้น
                    </label>
                  </div>
                </div>
              </div>

              {/* Row 2: หมวดหมู่ + สินค้า + ปริมาณ + หน่วย */}
              <div className="row mb-4">
                <div className="col-md-3">
                  <label className="form-label fw-bold">หมวดหมู่</label>
                  <select
                    className="form-select"
                    value={editSelectedGroup}
                    onChange={(e) => {
                      setEditSelectedGroup(e.target.value);
                      // ล้างสินค้าที่เลือกไว้เมื่อเปลี่ยนหมวดหมู่
                      setFormData({ ...formData, Item_Id: null, Item_Code: null, Item_Name: null, Step_Unit: "" });
                    }}
                    style={{ borderRadius: "8px", height: "38px" }}
                  >
                    <option value="">-- ทุกหมวดหมู่ --</option>
                    {groupList.map((g) => (
                      <option key={g.Group_Id} value={g.Group_Id}>
                        {g.Group_Name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-bold">สินค้า</label>
                  <SearchableSelect
                    value={formData.Item_Id || null}
                    options={itemList.filter((item) => !editSelectedGroup || String(item.Item_Group) === String(editSelectedGroup))}
                    onChange={(selected) => {
                      setFormData({ ...formData, Item_Id: selected?.Item_Id || null, Item_Code: selected?.Item_Code || null, Item_Name: selected?.Item_Name || null, Step_Unit: selected?.Item_Unit_ST || formData.Step_Unit });
                    }}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-bold">ปริมาณ</label>
                  <NumberInput
                    value={formData.Step_Qty || ""}
                    onValueChange={(value) => setFormData({ ...formData, Step_Qty: value })}
                    className="form-control text-end"
                    style={{ borderRadius: "8px", height: "38px" }}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-bold">หน่วย</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.Step_Unit || ""}
                    disabled
                    placeholder="หน่วยจากสินค้า"
                    style={{ borderRadius: "8px", height: "38px", backgroundColor: "#f9fafb" }}
                  />
                </div>
              </div>

              {/* Step Description */}
              <div className="mb-4">
                <label className="form-label fw-bold text-nowrap">รายละเอียดขั้นตอน</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.Step_Description || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, Step_Description: e.target.value })
                  }
                  style={{ borderRadius: "8px" }}
                  placeholder="ระบุรายละเอียดเพิ่มเติมของขั้นตอนการบริการ..."
                />
              </div>

            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="d-flex justify-content-end gap-2 mt-3">
          <button
            type="button"
            onClick={goBackToList}
            className="btn"
            style={{
              backgroundColor: "#f3f4f6",
              color: "#374151",
              borderRadius: "8px",
              padding: "10px 24px",
              border: "none",
            }}
            disabled={loading}
          >
            ยกเลิก
          </button>
          {mode === "U" && (
            <button
              type="button"
              onClick={handleDelete}
              className="btn text-white"
              style={{
                backgroundColor: "#dc2626",
                borderRadius: "8px",
                padding: "10px 24px",
                border: "none",
              }}
              disabled={loading}
            >
              ลบ
            </button>
          )}
          <button
            type="submit"
            className="btn text-white"
            style={{
              backgroundColor: "#E13F57",
              borderRadius: "8px",
              padding: "10px 24px",
              border: "none",
            }}
            disabled={loading}
          >
            {loading ? "กำลังบันทึก..." : mode === "I" ? "สร้าง" : "บันทึก"}
          </button>
        </div>
      </form>
    </>
  );
};

export default ServiceStepForm;

